import Modal from '../Modal/Modal';
import LoadingButton from '../../functional/LoadingButton/LoadingButton';

interface InstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStart?: () => void;
}

export const InstructionsModal = ({ isOpen, onClose, onStart }: InstructionsModalProps) => {
  return (
    <Modal.Wrapper isOpen={isOpen} onClose={onClose} showCloseButton closeable>
      <div className="p-6 space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Welcome to your Organizational Capacity Building Assessment
        </h2>
        
        <div className="space-y-4 text-gray-600">
          <p>
            Your organization is unique, with its own mission, vision, and values that inspire meaningful change. 
            That purpose is the heart of your work, it guides your decisions, fuels your motivation, and shapes 
            the difference you make.
          </p>
          
          <p>
            This assessment is designed to help you take a clear, constructive look at your strengths and 
            opportunities, providing insights and action steps you can adapt to your organization's specific 
            context and goals.
          </p>

          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="font-medium text-blue-900">
              🔍 Important Note: This is not a test, nor is it a performance evaluation. There are no right or wrong answers.
            </p>
          </div>

          <p>
            Instead, think of it as a guided reflection, a moment to pause and consider where your organization 
            is today and where you want it to be tomorrow.
          </p>

          <p>
            The insights you share will help create a tailored plan to strengthen your operations, programs/services, 
            and long-term sustainability. This is your pathway to purpose. Be yourself, stay curious, and enjoy the journey.
          </p>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">What You'll Be Doing</h3>
            <p>
              You'll answer a series of questions across key domains of a nonprofit organization. The assessment 
              usually takes about 30-45 minutes to complete and can be done on your own or with input from your 
              leadership team, staff, or board, whatever works best for your organization's size and structure.
            </p>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">How to Approach the Assessment</h3>
            <div className="bg-yellow-50 p-4 rounded-lg mb-4">
              <p className="font-medium text-yellow-900">
                🔍 Important Note: Research consistently shows that people often assess their competencies as 
                higher than they actually are.
              </p>
            </div>
            <ul className="list-disc pl-5 space-y-2">
              <li>Be reflective and candid. This is about your current reality, not your future goals.</li>
              <li>Avoid being overly critical or overly optimistic; aim for accuracy of your current state.</li>
              <li>If you're not 100% sure of a choice, please choose the previously listed option.</li>
            </ul>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">What Happens Next</h3>
            <p>
              Your responses will inform a practical, mission-aligned pathway to strengthen your organization's 
              development, effectiveness, and long-term impact.
            </p>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Final Thought</h3>
            <p>
              By engaging in this process, you're making an investment in your organization's future. Thank you 
              for your thoughtful participation and your commitment to building a stronger, more resilient 
              organization for the community you serve.
            </p>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          {onStart ? (
            <LoadingButton onClick={() => { onStart(); onClose(); }} className="w-full sm:w-auto">
              Let's Begin
            </LoadingButton>
          ) : (
            <LoadingButton onClick={onClose} className="w-full sm:w-auto">
              Close
            </LoadingButton>
          )}
        </div>
      </div>
    </Modal.Wrapper>
  );
};
